import {
  SortDirection,
  StringSearchFilterType,
} from '@warebee/frontend/data-access-api-graphql';
import { FilterPreset } from '@warebee/shared/common';
import {
  ActualityCongestionsParams,
  ActualityHqDataColumn,
  ActualityHqDataRow,
  ActualityHqDataRows,
  ActualityHqQueryBuilderParams,
  ActualityHqWithCongestionsField,
  ActualityHqWithCongestionsRows,
  getActualityHqDataRowsQuery,
  getActualityHqQueryBuilder,
} from '@warebee/shared/dataset-queries';
import { SelectQueryBuilder } from 'kysely';
import { atom, selector } from 'recoil';
import { AsyncLoadStatus, DataTableState } from '../../common/types';
import { filterPresetSelected } from '../../filterPreset/store/filterPreset.state';
import { viewerSelectedLocationIdAtom } from '../../layout/viewer/store/viewer.state';
import { warehouseSelectedId } from '../../store/warehouse.state';
import { actualityHeatmapRequestReadyInner } from './actuality.heatmap.state';
import {
  actualityEffectiveFilterConfig,
  actualityExtraSettings,
  actualityQueryBuilderParams,
  actualityQueryReuseHashSources,
} from './actuality.state';
import {
  ACTUALITY_CONGESTION_DISTANCE_DEFAULT,
  ACTUALITY_CONGESTION_THRESHOLD_SEC_DEFAULT,
} from './actuality.types';
import { executeDatasetQuery } from './feed.helper';
import { feedQueryBuilderIsReady } from './feed.state';
import { eventIssueFilterPreset } from '../constants/actuality.event.config';

/**
 * Actuality hyper cube data (feed joined with dataset ) state
 * - table data and table state
 */

const getKey = (postfix: string) => `warebee-actuality-hq-${postfix}`;

export const actualityHqBuilderParams = selector<ActualityHqQueryBuilderParams>(
  {
    key: getKey('hq-query-builder-params'),
    get: ({ get }) => {
      const actualityBuilderParamsBase = get(actualityQueryBuilderParams);
      const selectedPreset = get(
        filterPresetSelected,
      ) as FilterPreset<ActualityHqDataColumn>;
      const params: ActualityHqQueryBuilderParams = {
        ...actualityBuilderParamsBase,
        filterPresets: selectedPreset ? [selectedPreset] : [],
        filterConfig: get(actualityEffectiveFilterConfig),
      };
      return params;
    },
  },
);

export const actualityHqCongestionBuilderParams =
  selector<ActualityCongestionsParams>({
    key: getKey('hq-congestion-query-builder-params'),
    get: ({ get }) => {
      const extraSettings = get(actualityExtraSettings);
      const queryParamsBase = get(actualityHqBuilderParams);
      return {
        ...queryParamsBase,
        congestionThresholdSeconds:
          extraSettings?.congestionThresholdSeconds ??
          ACTUALITY_CONGESTION_THRESHOLD_SEC_DEFAULT,
        congestionDistance:
          extraSettings?.congestionDistance ??
          ACTUALITY_CONGESTION_DISTANCE_DEFAULT,
      };
    },
  });

export const actualityHqBuilderBase = selector<{
  b: SelectQueryBuilder<any, any, any>;
}>({
  key: getKey('hq-query-builder-base'),
  get: ({ get }) => {
    const queryParamsBase = get(actualityHqBuilderParams);
    const builder =
      getActualityHqQueryBuilder(queryParamsBase).selectFrom('hq');
    return { b: builder };
  },
});

export const actualityHqTableData = atom<ActualityHqDataRows>({
  key: getKey('hq-table-data'),
  default: [],
});

export const actualityHqTableTotalCount = atom<number>({
  key: getKey('hq-table-total-count'),
  default: 0,
});

export const actualityHqTableDataLoadStatus = atom<AsyncLoadStatus>({
  key: getKey('hq-table-data-status'),
  default: AsyncLoadStatus.None,
});

export const actualityHqTableDataState = atom<
  DataTableState<ActualityHqDataColumn>
>({
  key: getKey('hq-table-data-state'),
  default: {
    sortValues: {
      eventEndTime: SortDirection.DESC,
    },
    searchValues: {},
  },
});

export const actualityHqCongestionsTableData =
  atom<ActualityHqWithCongestionsRows>({
    key: getKey('hq-congestions-table-data'),
    default: [],
  });

export const actualityHqCongestionsTableTotalCount = atom<number>({
  key: getKey('hq-congestions-table-total-count'),
  default: 0,
});

export const actualityHqCongestionsTableDataLoadStatus = atom<AsyncLoadStatus>({
  key: getKey('hq-congestions-table-data-status'),
  default: AsyncLoadStatus.None,
});

export const actualityHqCongestionsTableDataState = atom<
  DataTableState<ActualityHqWithCongestionsField>
>({
  key: getKey('hq-congestions-table-data-state'),
  default: {
    sortValues: {
      eventEndTime: SortDirection.DESC,
    },
    searchValues: {},
  },
});

export const actualityLatestEvents = selector<ActualityHqDataRows>({
  key: getKey('latest-events'),
  get: async ({ get }) => {
    const warehouseId = get(warehouseSelectedId);
    if (!get(actualityHeatmapRequestReadyInner)) return null;

    const queryParamsBase = get(actualityHqBuilderParams);
    const eventsLimit = get(actualityEventsLimitAtom);
    const compiledQuery = getActualityHqDataRowsQuery({
      ...queryParamsBase,
      page: {
        skip: 0,
        limit: eventsLimit,
      },
      sortBy: [
        {
          direction: SortDirection.DESC,
          field: 'eventEndTime',
        },
      ],
    }).compile();

    if (!get(feedQueryBuilderIsReady)) return null;

    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: '[actuality] Latest Events',
      reuseHashSource: get(actualityQueryReuseHashSources),
    });
    return result;
  },
});

export const actualityLatestEventsWithIssues = selector<ActualityHqDataRows>({
  key: getKey('latest-events-with-issues'),
  get: async ({ get }) => {
    const warehouseId = get(warehouseSelectedId);
    if (!get(actualityHeatmapRequestReadyInner)) return null;

    const queryParamsBase = get(actualityHqBuilderParams);
    const eventsLimit = get(actualityEventsLimitAtom);

    const compiledQuery = getActualityHqDataRowsQuery({
      ...queryParamsBase,
      filterPresets: [...queryParamsBase.filterPresets, eventIssueFilterPreset],

      page: {
        skip: 0,
        limit: eventsLimit,
      },
      sortBy: [
        {
          direction: SortDirection.DESC,
          field: 'eventEndTime',
        },
      ],
    }).compile();

    if (!get(feedQueryBuilderIsReady)) return null;

    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: '[actuality] Latest Events with issues',
      reuseHashSource: get(actualityQueryReuseHashSources),
    });
    return result;
  },
});

// export const actualityLatestAgentEvents = selector<ActualityHqDataRows>({
//   key: getKey('latest-agent-events'),
//   get: async ({ get }) => {
//     if (!get(actualityHeatmapRequestReadyInner)) return null;
//     const warehouseId = get(warehouseSelectedId);
//     const agentId = get(feedSelectedAgentId);
//     const queryParamsBase = get(actualityHqBuilderParams);
//     const compiledQuery = getActualityHqDataRowsQuery({
//       ...queryParamsBase,
//       activity: {
//         ...queryParamsBase.activity,
//         agentId,
//       },
//       page: {
//         skip: 0,
//         limit: 100,
//       },
//       sortBy: [
//         {
//           direction: SortDirection.DESC,
//           field: 'eventEndTime',
//         },
//       ],
//     }).compile();

//     const [result] = await executeDatasetQuery({
//       warehouseId,
//       compiledQuery,
//       comment: '[actuality] Latest Agent Events',
//       reuseHashSource: get(actualityQueryReuseHashSources),
//     });
//     return result;
//   },
// });

export const actualityLatestLocationEvents = selector<ActualityHqDataRows>({
  key: getKey('latest-locations-events'),
  get: async ({ get }) => {
    const locationId = get(viewerSelectedLocationIdAtom);
    if (!get(actualityHeatmapRequestReadyInner)) return null;
    const warehouseId = get(warehouseSelectedId);
    const queryParamsBase = get(actualityHqBuilderParams);
    const eventsLimit = get(actualityEventsLimitAtom);
    const compiledQuery = getActualityHqDataRowsQuery({
      ...queryParamsBase,
      page: {
        skip: 0,
        limit: eventsLimit,
      },
      sortBy: [
        {
          direction: SortDirection.DESC,
          field: 'eventEndTime',
        },
      ],
      filterBy: {
        locationId: {
          value: locationId,
          type: StringSearchFilterType.CONTAINS,
        },
      },
    }).compile();

    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: '[actuality] Latest Location Events',
      reuseHashSource: get(actualityQueryReuseHashSources),
    });
    return result;
  },
});

export const actualitySelectedEvent = atom<ActualityHqDataRow>({
  key: getKey('selected-event'),
  default: null,
});

// Configurable limit for events
export const actualityEventsLimitAtom = atom<number>({
  key: getKey('events-limit'),
  default: 1000,
});

// Get total count of events (without limit) for comparison
export const actualityTotalEventsCount = selector<number>({
  key: getKey('total-events-count'),
  get: async ({ get }) => {
    const warehouseId = get(warehouseSelectedId);
    if (!get(actualityHeatmapRequestReadyInner)) return 0;

    const queryParamsBase = get(actualityHqBuilderParams);
    const compiledQuery = getActualityHqDataRowsQuery({
      ...queryParamsBase,
      page: {
        skip: 0,
        limit: 999999, // Large number to get total count
      },
      sortBy: [
        {
          direction: SortDirection.DESC,
          field: 'eventEndTime',
        },
      ],
    }).compile();

    if (!get(feedQueryBuilderIsReady)) return 0;

    const [result] = await executeDatasetQuery({
      warehouseId,
      compiledQuery,
      comment: '[actuality] Total Events Count',
      reuseHashSource: get(actualityQueryReuseHashSources),
    });
    return result?.length || 0;
  },
});
